import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import { Controlled as CodeMirrorView } from "react-codemirror2";
import { StriimModal, StriimTypography } from "@striim/striim-ui";
// @ts-ignore
import "cm/mode/sql/sql";
// @ts-ignore
import "cm/addon/edit/matchbrackets";
// @ts-ignore
import "cm/addon/edit/closebrackets";
// @ts-ignore
import "cm/addon/comment/comment";
// @ts-ignore
import "cm/addon/hint/show-hint";
// @ts-ignore
import "cm/addon/hint/sql-hint";
import { extractTQL, reconstructMessage, ExtractedTQL } from "./extract-tql";
import { codeMirrorEditorStylesFill } from "../../tools/tql-tools/codemirror-base.styles";

interface Props {
    open: boolean;
    value: string;
    onChange: (value: string) => void;
    onClose: () => void;
}

const styles = {
    iconBox: {
        width: 32,
        height: 32,
        borderRadius: "8px",
        backgroundColor: "primary.50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    titleLeft: {
        display: "flex",
        alignItems: "center",
        gap: 1.5,
    },
};

const TQLExpandModal: React.FC<Props> = ({ open, value, onChange, onClose }) => {
    const [localTql, setLocalTql] = useState("");
    const extractedRef = useRef<ExtractedTQL | null>(null);

    // Snapshot extraction once on open — prefix/suffix stay stable during edits
    useEffect(() => {
        if (open) {
            const ex = extractTQL(value);
            extractedRef.current = ex;
            setLocalTql(ex.tql);
        }
    }, [open]); // intentionally excludes `value`

    const handleDone = () => {
        if (extractedRef.current) {
            onChange(reconstructMessage(extractedRef.current, localTql));
        }
        onClose();
    };

    const handleCancel = () => {
        onClose(); // discard edits
    };

    const titleContent = (
        <Box sx={styles.titleRow}>
            <Box sx={styles.titleLeft}>
                <Box sx={styles.iconBox}>
                    <CodeIcon sx={{ fontSize: 16, color: "primary.main" }} />
                </Box>
                <StriimTypography variant="h2">TQL Editor</StriimTypography>
            </Box>
            <IconButton size="small" onClick={handleCancel}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );

    return (
        <StriimModal
            isVisible={open}
            size="large"
            titleContent={titleContent}
            confirmContent="Done"
            cancelContent="Cancel"
            onConfirm={handleDone}
            onCancel={handleCancel}
            dialogProps={{
                sx: {
                    "& .MuiDialog-paper": {
                        width: "80vw",
                        maxWidth: 960,
                        height: "72vh",
                        display: "flex",
                        flexDirection: "column",
                    },
                },
            }}
            dialogContentProps={{
                sx: {
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 0,
                    px: 3,
                    py: 2,
                },
            }}
        >
            <Box sx={codeMirrorEditorStylesFill}>
                <CodeMirrorView
                    value={localTql}
                    onBeforeChange={(_editor, _data, newValue) => setLocalTql(newValue)}
                    options={{
                        mode: "text/x-tql",
                        lineNumbers: true,
                        matchBrackets: true,
                        autoCloseBrackets: true,
                        extraKeys: { "Ctrl-/": "toggleComment" },
                    }}
                />
            </Box>
        </StriimModal>
    );
};

export default TQLExpandModal;
